import { Controller } from "@hotwired/stimulus"

// Renders a minimap for long Assistant conversations.
//
// Watches the messages container for new user messages and draws one tick per
// message on a vertical rail. Hovering the rail reveals a popover with every
// user message title (truncated). Clicking a tick or popover item scrolls the
// matching message into view. The "active" tick reflects the section the user
// is currently viewing — a section spans from one user message up to the next,
// so its assistant answer keeps the same tick highlighted.
//
// Values:
//   messagesSelector — CSS selector for the messages container (default "#messages")
//
// Targets:
//   rail    — container for the vertical list of ticks (required)
//   popover — container for the titles list, shown on hover (required)
//
// @markup-example:
// <div class="conversation-minimap hidden"
//      data-controller="conversation-minimap">
//   <div class="conversation-minimap__rail"    data-conversation-minimap-target="rail"></div>
//   <div class="conversation-minimap__popover" data-conversation-minimap-target="popover"></div>
// </div>
const MIN_USER_MESSAGES = 6
const LABEL_MAX_CHARS = 80
const SHOW_DELAY_MS = 350
const HIDE_DELAY_MS = 200
const ACTIVE_OFFSET_PX = 80

export default class extends Controller {
  static targets = ["rail", "popover"]
  static values = {
    messagesSelector: { type: String, default: "#messages" }
  }

  connect() {
    this.messagesContainer = document.querySelector(this.messagesSelectorValue)
    if (!this.messagesContainer || !this.hasRailTarget || !this.hasPopoverTarget) return

    this._popover = this.popoverTarget
    this.scrollRoot = document.querySelector('[data-messages-list-target="wrapper"]') || null
    this._entries = new Map()
    this._lastUserCount = -1
    this._hideTimer = null
    this._showTimer = null
    this._scrollRaf = null
    this._activeId = null

    this._mutationObserver = new MutationObserver(() => this._render())
    this._mutationObserver.observe(this.messagesContainer, { childList: true })

    this._onEnter = () => this._schedulePopoverShow()
    this._onLeave = () => this._schedulePopoverHide()
    this.element.addEventListener("mouseenter", this._onEnter)
    this.element.addEventListener("mouseleave", this._onLeave)
    this._popover.addEventListener("mouseenter", this._onEnter)
    this._popover.addEventListener("mouseleave", this._onLeave)

    this._onScroll = () => {
      if (this._scrollRaf) return
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null
        this._updateActive()
      })
    }
    this._scrollTarget = this.scrollRoot || window
    this._scrollTarget.addEventListener("scroll", this._onScroll, { passive: true })

    this._render()
  }

  disconnect() {
    this._mutationObserver?.disconnect()
    if (this._hideTimer) clearTimeout(this._hideTimer)
    if (this._showTimer) clearTimeout(this._showTimer)
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf)
    this.element.removeEventListener("mouseenter", this._onEnter)
    this.element.removeEventListener("mouseleave", this._onLeave)
    this._popover?.removeEventListener("mouseenter", this._onEnter)
    this._popover?.removeEventListener("mouseleave", this._onLeave)
    this._scrollTarget?.removeEventListener("scroll", this._onScroll)
  }

  scrollTo(event) {
    const id = event.currentTarget.dataset.messageId
    const entry = this._entries.get(id)
    entry?.message.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  highlight(event) {
    const id = event.currentTarget.dataset.messageId
    this._entries.forEach((entry, entryId) => {
      entry.item.classList.toggle("is-hover", entryId === id)
    })
  }

  clearHighlight() {
    this._entries.forEach((entry) => entry.item.classList.remove("is-hover"))
  }

  _schedulePopoverShow() {
    if (this._hideTimer) {
      clearTimeout(this._hideTimer)
      this._hideTimer = null
    }
    if (this._popover.classList.contains("is-visible")) return
    if (this._showTimer) return
    this._showTimer = setTimeout(() => {
      this._popover.classList.add("is-visible")
      this._popover.scrollTop = this._popover.scrollHeight
      this._showTimer = null
    }, SHOW_DELAY_MS)
  }

  _schedulePopoverHide() {
    if (this._showTimer) {
      clearTimeout(this._showTimer)
      this._showTimer = null
    }
    if (this._hideTimer) clearTimeout(this._hideTimer)
    this._hideTimer = setTimeout(() => {
      this._popover.classList.remove("is-visible")
      this._hideTimer = null
    }, HIDE_DELAY_MS)
  }

  _render() {
    const userMessages = this.messagesContainer.querySelectorAll(
      '.assistant--message[data-role="user"]'
    )

    if (userMessages.length <= MIN_USER_MESSAGES) {
      this.element.classList.add("hidden")
      this._lastUserCount = userMessages.length
      return
    }

    if (userMessages.length === this._lastUserCount) return
    this._lastUserCount = userMessages.length

    this.element.classList.remove("hidden")
    this.railTarget.innerHTML = ""
    this.popoverTarget.innerHTML = ""
    this._entries.clear()
    this._activeId = null

    userMessages.forEach((message, index) => {
      const id = message.dataset.messageId
      if (!id) return

      const content = message.querySelector(".assistant--message-content")
      const label = (content?.textContent || "").trim().slice(0, LABEL_MAX_CHARS)
      const createdAgo = message.dataset.createdAgo

      const tick = document.createElement("button")
      tick.type = "button"
      tick.className = "conversation-minimap__tick"
      tick.dataset.messageId = id
      tick.dataset.action = [
        "click->conversation-minimap#scrollTo",
        "mouseenter->conversation-minimap#highlight",
        "mouseleave->conversation-minimap#clearHighlight"
      ].join(" ")
      this.railTarget.appendChild(tick)

      const item = document.createElement("button")
      item.type = "button"
      item.className = "conversation-minimap__item"
      item.dataset.messageId = id
      item.dataset.action = "click->conversation-minimap#scrollTo"

      const indexEl = document.createElement("span")
      indexEl.className = "conversation-minimap__item-index"
      indexEl.textContent = `${index + 1}.`
      item.appendChild(indexEl)

      const labelEl = document.createElement("span")
      labelEl.className = "conversation-minimap__item-label"
      labelEl.textContent = label
      item.appendChild(labelEl)

      if (createdAgo) {
        const timeEl = document.createElement("span")
        timeEl.className = "conversation-minimap__item-time"
        timeEl.textContent = createdAgo
        item.appendChild(timeEl)
      }

      this.popoverTarget.appendChild(item)

      this._entries.set(id, { message, tick, item })
    })

    this._updateActive()
  }

  // The active section spans from a user message up to (but not including) the
  // next user message. So the active tick is the last user message whose top
  // has scrolled past the activation line near the top of the viewport.
  _updateActive() {
    const referenceTop = this.scrollRoot
      ? this.scrollRoot.getBoundingClientRect().top + ACTIVE_OFFSET_PX
      : ACTIVE_OFFSET_PX

    let activeId = null
    this._entries.forEach((entry, id) => {
      if (entry.message.getBoundingClientRect().top <= referenceTop) {
        activeId = id
      }
    })

    if (activeId === this._activeId) return
    if (this._activeId) {
      const previous = this._entries.get(this._activeId)
      previous?.tick.classList.remove("is-active")
      previous?.item.classList.remove("is-active")
    }
    if (activeId) {
      const next = this._entries.get(activeId)
      next?.tick.classList.add("is-active")
      next?.item.classList.add("is-active")
    }
    this._activeId = activeId
  }
}
